import { Platform } from '@angular/cdk/platform';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MtnaLogger } from '@mtna/core-angular';
import { RdsApiService } from '@rds/shared-api';
import { Observable, of, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';

/**
 * Backend model:
 * https://bitbucket.org/mtnaus/rds/src/develop/src/main/java/us/mtna/rds/ws/InformationController.java
 */
@Injectable({
  providedIn: 'root',
})
export class RdsServerService {
  /** base url for the service */
  protected get SERVER_URL(): string {
    let apiUrl: string = this.apiService.getApiUrl() || '';
    if (!apiUrl?.endsWith('/app-api')) {
      // Support backwards compatibility
      // The API url should no longer end with '/api',
      // but point to the root of the rds instance.
      apiUrl += '/app-api';
    }
    return `${apiUrl}/server`;
  }

  protected _basePath: string | undefined;
  protected _disclaimerCache$ = new ReplaySubject<string>(1);
  protected _disclaimerRequest$: Observable<string> | undefined;

  constructor(
    protected apiService: RdsApiService,
    protected http: HttpClient,
    protected logger: MtnaLogger,
    protected platform: Platform
  ) {}

  /**
   * Returns a bath path for building consumer urls.
   * @param refresh Whether to clear any cached path.
   * @returns the base url path.
   * @remarks This method uses caching, returns a cold observable.
   */
  getBasePath(refresh?: boolean): Observable<string> {
    if (refresh || !this._basePath) {
      return this.http.get<string>(`${this.SERVER_URL}/base/path`).pipe(tap((path) => (this._basePath = path)));
    }
    return of(this._basePath);
  }

  /**
   * Get the disclaimer (EULA) to display to users who want to use the RDS API.
   * If there is no EULA in effect, the response will be null.
   * @remarks This method uses caching, returns a hot observable.
   * @param refresh forces a cache refresh, will update all subscribers. Defaults to false.
   * @returns the EULA or null if not applicable
   */
  getDisclaimer(refresh: boolean = false): Observable<string> {
    if (refresh || !this._disclaimerRequest$) {
      this.logger.debug(`[RdsServerService] getDisclaimer - Fetching, ${refresh ? 'forced refresh' : 'not in cache'}`);
      this._disclaimerRequest$ = this.http.get<string>(`${this.SERVER_URL}/disclaimer`);
      this._disclaimerRequest$.subscribe(
        (result) => this._disclaimerCache$.next(result),
        (err) => this._disclaimerCache$.error(err)
      );
    }
    return this._disclaimerCache$.asObservable();
  }

  /**
   * Checks to see if the manager users guide exists
   */
  checkManagerGuide(): Observable<boolean> {
    this.logger.debug(`[RdsServerService] check for manager guide`);
    return this.http.get<boolean>(`${this.SERVER_URL}/documentation/guide/manager/exists`);
  }

  /**
   * Get the Manager application user's guide.
   */
  getManagerGuide(): void {
    const a = document.createElement('a');
    a.setAttribute('href', `${this.SERVER_URL}/documentation/guide/manager`);
    a.setAttribute('target', '_blank');
    a.setAttribute('download', '');
    document.body.appendChild(a);
    this.logger.log('[@rds/components] RdsServerService#downloadManagerGuide - generated anchor:', a);
    a.click();

    if (this.platform.SAFARI) {
      this.logger.log('Safari detected, using window.location.href to download file.');
      window.location.href = a.href;
    }
    a.parentElement?.removeChild(a);
  }

  /**
   * Checks to see if the manager users guide exists
   */
  checkExplorerGuide(): Observable<boolean> {
    this.logger.debug(`[RdsServerService] check for explorer guide `);
    return this.http.get<boolean>(`${this.SERVER_URL}/documentation/guide/explorer/exists`);
  }

  /**
   * Get the Explorer application user's guide.
   */
  getExplorerGuide(): void {
    const a = document.createElement('a');
    a.setAttribute('href', `${this.SERVER_URL}/documentation/guide/explorer`);
    a.setAttribute('target', '_blank');
    a.setAttribute('download', '');
    document.body.appendChild(a);
    this.logger.log('[@rds/components] RdsServerService#downloadExplorerGuide - generated anchor:', a);
    a.click();

    if (this.platform.SAFARI) {
      this.logger.log('Safari detected, using window.location.href to download file.');
      window.location.href = a.href;
    }
    a.parentElement?.removeChild(a);
  }

  /**
   * Checks to see if the manager users guide exists
   */
  checkTabulationEngineGuide(): Observable<boolean> {
    this.logger.debug(`[RdsServerService] check for tabulation engine guide`);
    return this.http.get<boolean>(`${this.SERVER_URL}/documentation/guide/tabengine/exists`);
  }

  /**
   * Get the Tabulation Engine application user's guide.
   */
  getTabulationEngineGuide(): void {
    const a = document.createElement('a');
    a.setAttribute('href', `${this.SERVER_URL}/documentation/guide/tabengine`);
    a.setAttribute('target', '_blank');
    a.setAttribute('download', '');
    document.body.appendChild(a);
    this.logger.log('[@rds/components] RdsServerService#downloadTabulationEnginerGuide - generated anchor:', a);
    a.click();

    if (this.platform.SAFARI) {
      this.logger.log('Safari detected, using window.location.href to download file.');
      window.location.href = a.href;
    }
    a.parentElement?.removeChild(a);
  }
}
